import { useState } from "react";
import axios from "axios";
import "./App.css";

function App() {
  const [keyword, setKeyword] = useState("");
  const [dictionaryData, setDictionaryData] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [audioError, setAudioError] = useState("");
  const [audioUrl, setAudioUrl] = useState("");

  function updateKeyword(event) {
    setKeyword(event.target.value);
  }

  function searchWord(word) {
    const searchedWord = word.trim();

    if (!searchedWord) {
      setError("Please enter a word.");
      return;
    }

    setLoading(true);
    setError("");
    setAudioError("");
    setAudioUrl("");
    setDictionaryData(null);
    setPhotos([]);

    const dictionaryApiUrl = `https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(
      searchedWord
    )}`;

    axios
      .get(dictionaryApiUrl)
      .then(handleResponse)
      .catch(handleError);

    const pexelsApiUrl = `https://api.pexels.com/v1/search?query=${encodeURIComponent(
      searchedWord
    )}&per_page=3`;

    axios
      .get(pexelsApiUrl, {
        headers: {
          Authorization: process.env.REACT_APP_PEXELS_API_KEY,
        },
      })
      .then(handlePhotosResponse)
      .catch(handlePhotosError);
  }

  function handleSubmit(event) {
    event.preventDefault();
    searchWord(keyword);
  }

  function handleResponse(response) {
    const result = response.data[0];

    const pronunciation = result.phonetics?.find(
      (item) => item.audio && item.audio.trim() !== ""
    );

    let pronunciationUrl = pronunciation
      ? pronunciation.audio
      : "";

    if (pronunciationUrl.startsWith("//")) {
      pronunciationUrl = `https:${pronunciationUrl}`;
    }

    setDictionaryData(result);
    setKeyword(result.word);
    setAudioUrl(pronunciationUrl);
    setLoading(false);
    setError("");
    setAudioError("");
  }

  function handleError(error) {
    setDictionaryData(null);
    setAudioUrl("");
    setPhotos([]);
    setLoading(false);
  
    if (error.response?.status === 404) {
      setError(
        "We couldn't find that word. Please check the spelling or try another word."
      );
    } else {
      setError(
        "Sorry, we couldn't load that word right now. Please try again."
      );
    }
  }

  function handlePhotosResponse(response) {
    setPhotos(response.data.photos || []);
  }

  function handlePhotosError() {
    setPhotos([]);
  }

  function searchSuggestedWord(word) {
    setKeyword(word);
    searchWord(word);
  }

  function goHome() {
    setKeyword("");
    setDictionaryData(null);
    setPhotos([]);
    setError("");
    setAudioError("");
    setAudioUrl("");
    setLoading(false);
  }

  function getPhonetic() {
    if (!dictionaryData) {
      return "";
    }

    if (dictionaryData.phonetic) {
      return dictionaryData.phonetic;
    }

    const phonetic = dictionaryData.phonetics?.find(
      (item) => item.text
    );

    return phonetic ? phonetic.text : "";
  }

  function speakWord() {
    if (!dictionaryData?.word) {
      setAudioError("Pronunciation is not available.");
      return;
    }

    if (!window.speechSynthesis) {
      setAudioError(
        "Pronunciation is not supported by this browser."
      );
      return;
    }

    setAudioError("");

    const speech = new SpeechSynthesisUtterance(
      dictionaryData.word
    );

    speech.lang = "en-AU";
    speech.rate = 0.85;

    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(speech);
  }

  function playAudio() {
    setAudioError("");

    if (!audioUrl) {
      speakWord();
      return;
    }

    const audio = new Audio(audioUrl);

    audio.play().catch(() => {
      speakWord();
    });
  }

  return (
    <div className="App">
      <main className="dictionary-app">
        <header>
          <button
            type="button"
            className="dictionary-label"
            onClick={goHome}
          >
            📖 DICTIONARY
          </button>

          <h1>
            Find the words
            <br />
            <em>you're looking for.</em>
          </h1>

          <p className="intro">
            Search any English word for definitions, phonetics, and more.
          </p>
        </header>

        <form
          className="search-form"
          onSubmit={handleSubmit}
        >
          <input
            type="search"
            placeholder="Search a word..."
            value={keyword}
            onChange={updateKeyword}
            aria-label="Search for a word"
          />

          <button
            type="submit"
            className="search-button"
            aria-label="Search"
          >
            🔍
          </button>
        </form>

        {!dictionaryData && !loading && !error && (
          <div className="suggestions">
            <span className="try-label">Try:</span>

            <button
              type="button"
              onClick={() =>
                searchSuggestedWord("serendipity")
              }
            >
              serendipity
            </button>

            <button
              type="button"
              onClick={() =>
                searchSuggestedWord("ephemeral")
              }
            >
              ephemeral
            </button>

            <button
              type="button"
              onClick={() =>
                searchSuggestedWord("wanderlust")
              }
            >
              wanderlust
            </button>
          </div>
        )}

        {loading && (
          <p className="loading-message">
            Looking up "{keyword}"...
          </p>
        )}

        {error && (
          <div className="error-message">
            <p>{error}</p>

            <button
              type="button"
              onClick={goHome}
            >
              Try another word
            </button>
          </div>
        )}

        {dictionaryData && (
          <section className="results">
            <div className="word-card">
              <div>
                <h2>{dictionaryData.word}</h2>

                {getPhonetic() && (
                  <p className="phonetic">
                    {getPhonetic()}
                  </p>
                )}
              </div>

              <div className="pronunciation">
                <button
                  type="button"
                  className="audio-button"
                  onClick={playAudio}
                  aria-label={`Play pronunciation of ${dictionaryData.word}`}
                  title="Play pronunciation"
                >
                  ▶
                </button>
              </div>
            </div>

            {audioError && (
              <p className="audio-error">
                {audioError}
              </p>
            )}

            {dictionaryData.meanings?.map(
              (meaning, meaningIndex) => (
                <div
                  className="meaning-card"
                  key={`${meaning.partOfSpeech}-${meaningIndex}`}
                >
                  <div className="meaning-heading">
                    <span className="part-of-speech">
                      {meaning.partOfSpeech}
                    </span>

                    <span className="heading-line"></span>
                  </div>

                  <div className="definitions">
                    {meaning.definitions
                      ?.slice(0, 3)
                      .map(
                        (
                          definition,
                          definitionIndex
                        ) => (
                          <div
                            className="definition"
                            key={definitionIndex}
                          >
                            <p className="definition-text">
                              <span className="definition-marker">
                                —
                              </span>

                              {definition.definition}
                            </p>

                            {definition.example && (
                              <p className="example">
                                “{definition.example}”
                              </p>
                            )}
                          </div>
                        )
                      )}
                  </div>

                  {meaning.synonyms?.length > 0 && (
                    <div className="word-list">
                      <p className="word-list-label">
                        Synonyms
                      </p>

                      <div className="word-tags">
                        {meaning.synonyms
                          .slice(0, 6)
                          .map((synonym) => (
                            <button
                              type="button"
                              key={synonym}
                              onClick={() =>
                                searchSuggestedWord(
                                  synonym
                                )
                              }
                            >
                              {synonym}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}

                  {meaning.antonyms?.length > 0 && (
                    <div className="word-list">
                      <p className="word-list-label">
                        Antonyms
                      </p>

                      <div className="word-tags antonyms">
                        {meaning.antonyms
                          .slice(0, 6)
                          .map((antonym) => (
                            <button
                              type="button"
                              key={antonym}
                              onClick={() =>
                                searchSuggestedWord(
                                  antonym
                                )
                              }
                            >
                              {antonym}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )
            )}

            {photos.length > 0 && (
              <div className="photos-section">
                <p className="photos-label">
                  Related Photos
                </p>

                <div className="photos-grid">
                  {photos.map((photo) => (
                    <div
                      className="photo-card"
                      key={photo.id}
                    >
                      <a
                        href={photo.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                       <img
                        src={photo.src.large}
                       alt={photo.alt || dictionaryData.word}
                      />
                      </a>

                      <p>
                        Photo by{" "}
                        <a
                          href={
                            photo.photographer_url
                          }
                          target="_blank"
                          rel="noreferrer"
                        >
                          {photo.photographer}
                        </a>
                      </p>
                    </div>
                  ))}
                </div>

                <p className="pexels-credit">
                  Photos provided by{" "}
                  <a
                    href="https://www.pexels.com"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Pexels
                  </a>
                </p>
              </div>
            )}
          </section>
        )}

        <footer>
          <p>
            Coded by{" "}
            <a
              href="https://github.com/rzmeridores"
              target="_blank"
              rel="noreferrer"
            >
              Ranamae Meridores
            </a>
            , and{" "}
            <a
              href="https://github.com/rzmeridores/react-dictionary-search-engine"
              target="_blank"
              rel="noreferrer"
            >
              open-sourced on GitHub
            </a>
            .
          </p>
        </footer>
      </main>
    </div>
  );
}

export default App;